import {
  CURRENCY_BY_MARKET,
  SEED_MARKETS,
  marketsForScope,
  type Currency,
  type FraudStatus,
  type Market,
  type Ticket,
  type TicketSource,
  type TicketStatus,
  type TicketTrigger,
  type User,
} from '../dataProvider/types';

/**
 * The Ticket inbox query/aggregation module (D-ticket-inbox, the Phase 6 lead) - the
 * ONE place tickets are shaped for the worklist. Same seam idiom as the chargebacks
 * worklist, but reading a NEW first-class `tickets` collection rather than filtering an
 * existing entity (a ticket is its own record, unlike a dispute which rides on a
 * transaction). v1 aggregates client-side from the mock provider; the real-data
 * migration swaps this module for a server-side endpoint without the surface changing.
 *
 * The SLA BREACH is DERIVED off the injected clock (now past slaTargetAt and not
 * resolved), NOT a stored flag (the suspensions elapsed-derivation pattern). Each row
 * carries the member joined in (name, true signed balance, fraud status) so the member-
 * helping actions (the support credit, the suspend) work from the ticket context.
 *
 * REGION: OPERATIONAL and SWITCHER-SCOPED (D-ticket-inbox), NOT global. A ticket is
 * about one member in one market, so the inbox follows the region switcher scope via
 * marketsForScope, exactly like the chargebacks and cashout queues. A single-region
 * support agent sees only their market's tickets.
 */

export interface TicketMember {
  id: number;
  name: string;
  /** The member email, joined in so a canned response can substitute {member_email}. */
  email: string;
  market: Market;
  balanceSigned: number;
  fraudStatus: FraudStatus;
}

export interface TicketRow {
  id: number;
  userId: number;
  memberName: string;
  market: Market;
  currency: Currency;
  status: TicketStatus;
  assignee: string | null;
  source: TicketSource;
  triggeringEntity: TicketTrigger | null;
  subject: string;
  body: string;
  slaTargetAt: string;
  createdAt: string;
  /** Derived off the injected clock: past the SLA target and not resolved. */
  breached: boolean;
  /** The joined member, for the credit / suspend actions from the ticket context. */
  member: TicketMember;
}

export interface TicketInboxView {
  open: TicketRow[];
  escalated: TicketRow[];
  pending: TicketRow[];
  resolved: TicketRow[];
}

export interface TicketInboxInput {
  tickets: Ticket[];
  users: User[];
}

export interface TicketInboxOptions {
  /** Access scope (UK | US | GLOBAL) from the region switcher. */
  scope: string;
  /** The injected clock, so the SLA-breach derivation is deterministic in tests. */
  now: number;
}

/** The markets a scope renders, kept to the operating markets (the chargebacks pattern). */
const marketsFor = (scope: string): Market[] =>
  marketsForScope(scope).filter((m) => SEED_MARKETS.includes(m));

/** Breached first, then soonest-due first, then id. */
const worklistOrder = (a: TicketRow, b: TicketRow): number =>
  Number(b.breached) - Number(a.breached) ||
  a.slaTargetAt.localeCompare(b.slaTargetAt) ||
  a.id - b.id;

/** Resolved is history: newest first. */
const newestFirst = (a: TicketRow, b: TicketRow): number =>
  b.slaTargetAt.localeCompare(a.slaTargetAt) || b.id - a.id;

export function buildTicketInboxView(
  input: TicketInboxInput,
  opts: TicketInboxOptions,
): TicketInboxView {
  const markets = marketsFor(opts.scope);
  const usersById = new Map(input.users.map((u) => [u.id, u]));

  const rows: TicketRow[] = input.tickets
    .filter((t) => markets.includes(t.market))
    .map((t) => {
      const user = usersById.get(t.userId);
      const breached =
        t.status !== 'resolved' && opts.now > new Date(t.slaTargetAt).getTime();
      return {
        id: t.id,
        userId: t.userId,
        memberName: user?.name ?? '',
        market: t.market,
        currency: CURRENCY_BY_MARKET[t.market],
        status: t.status,
        assignee: t.assignee ?? null,
        source: t.source,
        triggeringEntity: t.triggeringEntity ?? null,
        subject: t.subject,
        body: t.body,
        slaTargetAt: t.slaTargetAt,
        createdAt: t.createdAt,
        breached,
        member: {
          id: t.userId,
          name: user?.name ?? '',
          email: user?.email ?? '',
          market: t.market,
          balanceSigned: Number(user?.balanceSigned ?? 0),
          fraudStatus: (user?.fraudStatus ?? 'active') as FraudStatus,
        },
      };
    });

  return {
    open: rows.filter((r) => r.status === 'open').sort(worklistOrder),
    escalated: rows.filter((r) => r.status === 'escalated').sort(worklistOrder),
    pending: rows.filter((r) => r.status === 'pending').sort(worklistOrder),
    resolved: rows.filter((r) => r.status === 'resolved').sort(newestFirst),
  };
}
